const DATA_BR_REGEX = /^\d{2}\/\d{2}\/\d{4}$/;

export type CampoNormalizavel = {
  readonly tipo: string;
};

/**
 * Formata uma Date para string no padrão DD/MM/YYYY.
 */
export const formatarDataBr = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Converte Date, ISO ou string já formatada para DD/MM/YYYY.
 */
export const normalizeDataResposta = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? '' : formatarDataBr(value);
  }
  if (typeof value !== 'string') {
    return '';
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (DATA_BR_REGEX.test(trimmed)) {
    return trimmed;
  }
  const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return `${isoDateMatch[3]}/${isoDateMatch[2]}/${isoDateMatch[1]}`;
  }
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return formatarDataBr(parsed);
  }
  return '';
};

/**
 * Indica se o valor de Data é aceitável na validação do formulário.
 */
export const isDataRespostaValida = (value: unknown): boolean => {
  if (value instanceof Date) {
    return !Number.isNaN(value.getTime());
  }
  if (typeof value !== 'string' || !value.trim()) {
    return false;
  }
  const trimmed = value.trim();
  if (DATA_BR_REGEX.test(trimmed)) {
    const [day, month, year] = trimmed.split('/').map(Number);
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }
  const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDateMatch) {
    return true;
  }
  const parsed = new Date(trimmed);
  return !Number.isNaN(parsed.getTime());
};

/**
 * Indica se o valor possui metadados de arquivo já enviados ao backend.
 */
export const isArquivoMetaValido = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const arquivo = value as Record<string, unknown>;
  return (
    arquivo.id !== undefined &&
    arquivo.id !== null &&
    arquivo.nome !== undefined &&
    arquivo.url !== undefined
  );
};

/**
 * Indica se o valor é um array de metadados de arquivo válidos.
 */
export const isMultiplosArquivosMetaValido = (value: unknown): boolean => {
  if (!Array.isArray(value) || value.length === 0) {
    return false;
  }
  return value.every(item => isArquivoMetaValido(item));
};

const isAlternativaObjeto = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  const alternativa = value as Record<string, unknown>;
  const hasLabelValue =
    alternativa.label !== undefined && alternativa.value !== undefined;
  const hasTituloValor =
    alternativa.titulo !== undefined || alternativa.valor !== undefined;
  return hasLabelValue || hasTituloValor;
};

const normalizeAlternativaResposta = (value: unknown): unknown => {
  if (!isAlternativaObjeto(value)) {
    return value;
  }
  const alternativa = value as Record<string, unknown>;
  if (alternativa.label !== undefined && alternativa.value !== undefined) {
    return {
      label: alternativa.label,
      value: alternativa.value,
    };
  }
  return {
    label: alternativa.titulo ?? alternativa.label,
    value: alternativa.valor ?? alternativa.value,
  };
};

/**
 * Normaliza a resposta de um campo antes do POST em /flow/responder-formulario/.
 */
export const normalizeRespostaCampo = (
  field: CampoNormalizavel,
  value: unknown,
): unknown => {
  if (value === null || value === undefined) {
    return value === undefined ? '' : value;
  }
  if (field.tipo === 'Data') {
    return normalizeDataResposta(value);
  }
  if (field.tipo === 'Arquivo' || field.tipo === 'Multiplos_Arquivos') {
    return value;
  }
  if (
    field.tipo === 'Alternativas' ||
    field.tipo === 'Alternativas_API' ||
    field.tipo === 'Municipio' ||
    field.tipo === 'Instituicao_Ensino' ||
    field.tipo === 'Modalidade'
  ) {
    return normalizeAlternativaResposta(value);
  }
  if (field.tipo === 'Matriz' || field.tipo.startsWith('Multiplos')) {
    return value;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    if (value instanceof Date) {
      return normalizeDataResposta(value);
    }
    console.warn(
      '[Flow] Objeto sem formato conhecido descartado no envio',
      field.tipo,
      value,
    );
    return '';
  }
  return value;
};
